import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  CORNELL_CHOICES,
  initialCornelFormData,
} from "./cornellDepressionScale.config";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import DynamicSelectInput from "../../../../../FormFields/CustomSelect/DynamicSelectInput";
import { createCornelDepression } from "../../../../../../api/services/assessment";
import { getCarePlans } from "../../../../../../api/services/core";
import { QK } from "../../../../../../base/qk";

const CreateCornellDepressionScaleModal = ({ open, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();

  const { data: carePlans } = useQuery([QK.carePlans], () => getCarePlans());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialCornelFormData,
  });

  const createAssessmentMutation = useMutation(
    (assessmentData) => createCornelDepression(assessmentData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QK.cornelDepressions, clientID]);
        enqueueSnackbar("Cornell Depression Scale created successfully", {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to create assessment",
          { variant: "error" }
        );
      },
    }
  );

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        client: clientID,
      };
      await createAssessmentMutation.mutateAsync(payload);
      handleCloseModal();
    } catch (error) {
      // Error is handled by the mutation's onError callback
    }
  };

  const handleCloseModal = () => {
    reset();
    handleClose();
  };

  const renderSelectField = (name, label) => (
    <Grid item xs={12} md={6} key={name}>
      <Controller
        name={name}
        control={control}
        rules={{ required: `${label} is required` }}
        render={({ field }) => (
          <CustomSelect
            name={name}
            label={`${label} *`}
            selectedValue={field.value}
            onChange={field.onChange}
            options={CORNELL_CHOICES}
            error={!!errors[name]}
            helperText={errors[name]?.message}
            required
          />
        )}
      />
    </Grid>
  );

  const fields = [
    {
      name: "anxiety",
      label: "Anxiety; anxious expression, rumination, worrying",
    },
    {
      name: "sadness",
      label: "Sadness; sad expression, sad voice, tearfulness",
    },
    { name: "lack_of_reaction", label: "Lack of reaction to pleasant events" },
    { name: "irritability", label: "Irritability; annoyed, short tempered" },
    {
      name: "agitation",
      label: "Agitation; restlessness, hand wringing, hair pulling",
    },
    {
      name: "retardation",
      label: "Retardation; slow movements, slow speech, slow reactions",
    },
    {
      name: "multiple_physical_complaints",
      label: "Multiple physical complaints",
    },
    {
      name: "loss_of_interest",
      label: "Loss of interest, less involved in usual activities",
    },
    { name: "appetite_loss", label: "Appetite loss; eating less than usual" },
    {
      name: "weight_loss",
      label: "Weight loss (score 2 if > 5 lb in one month)",
    },
    { name: "lack_of_energy", label: "Lack of energy, fatigues easily" },
    { name: "diurnal_variation_of_mood", label: "Diurnal variation of mood" },
    { name: "difficulty_falling_asleep", label: "Difficulty falling asleep" },
    { name: "multiple_awakenings", label: "Multiple awakenings during sleep" },
    { name: "early_morning_awakening", label: "Early morning awakening" },
    {
      name: "suicidal_feelings",
      label: "Suicidal; feels life is not worth living",
    },
    {
      name: "poor_self_esteem",
      label: "Poor self-esteem; self-blame, feelings of failure",
    },
    { name: "pessimism", label: "Pessimism; anticipation of the worst" },
    { name: "mood_congruent_delusions", label: "Mood congruent delusions" },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      scroll="body"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Create Cornell Depression Scale Assessment
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ p: 2 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="care_plan"
                  control={control}
                  rules={{ required: "Care plan is required" }}
                  render={({ field }) => (
                    <DynamicSelectInput
                      options={carePlans}
                      label="Which care plan is this linked to? *"
                      name="care_plan"
                      onChange={field.onChange}
                      value={field.value}
                      error={!!errors.care_plan}
                      helperText={errors.care_plan?.message}
                      required
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Assessment Questions
            </Typography>

            <Grid container spacing={2}>
              {fields.map((field) =>
                renderSelectField(field.name, field.label)
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="comments"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="comments"
                      label="Comments"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={4}
                      placeholder="Enter any additional comments here."
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseModal} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          disabled={createAssessmentMutation.isLoading}
        >
          {createAssessmentMutation.isLoading
            ? "Creating..."
            : "Create Assessment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCornellDepressionScaleModal;
